import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useIntake } from '../../context/IntakeContext';
import { Button } from '../ui/Button';
import { CheckIcon, Edit2Icon, SendIcon, DownloadIcon, Loader2Icon, ShieldCheckIcon } from 'lucide-react';
import { MascotPanel } from '../ui/MascotPanel';
import { generateIntakeHTML } from '../../utils/generateIntakeHTML';
import { submitIntake } from '../../utils/submitIntake';
export function Step15Summary() {
  const {
    data,
    setCurrentStep
  } = useIntake();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const SummarySection = ({
    title,
    step,
    children
  }: {
    title: string;
    step: number;
    children: React.ReactNode;
  }) => <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6" data-id="element-162">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-163">
        <h3 className="font-bold text-gray-900" data-id="element-164">{title}</h3>
        <button onClick={() => setCurrentStep(step)} className="text-sm font-medium text-[#007AFF] hover:text-[#005FCC] flex items-center" data-id="element-165">
          <Edit2Icon className="w-4 h-4 mr-1" data-id="element-166" />
          Bewerken
        </button>
      </div>
      <div className="p-6 space-y-2" data-id="element-167">{children}</div>
    </div>;
  const DataRow = ({
    label,
    value
  }: {
    label: string;
    value: string | React.ReactNode;
  }) => <div className="flex flex-col sm:flex-row sm:py-2 border-b border-gray-100 last:border-0" data-id="element-168">
      <span className="text-sm font-medium text-gray-500 sm:w-1/3 mb-1 sm:mb-0" data-id="element-169">
        {label}
      </span>
      <span className="text-sm text-gray-900 sm:w-2/3 font-medium" data-id="element-170">
        {value || <span className="text-gray-400 italic" data-id="element-171">Niet ingevuld</span>}
      </span>
    </div>;
  const handleSubmit = async () => {
    if (!acceptedPrivacy) {
      alert('Ga akkoord met de privacyvoorwaarden om verder te gaan.');
      return;
    }
    setIsSubmitting(true);
    const htmlContent = generateIntakeHTML(data);
    const clientEmail = data.contactEmail || 'klant@voorbeeld.nl';
    const clientName = data.companyName || data.contactName || 'Nieuwe Klant';
    await submitIntake(htmlContent, clientEmail, clientName);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 1500);
  };
  const handleDownloadPDF = () => {
    const htmlContent = generateIntakeHTML(data);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } else {
      alert('Pop-ups worden geblokkeerd. Sta pop-ups toe om de PDF te downloaden.');
    }
  };
  if (isSuccess) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="max-w-2xl mx-auto text-center py-12" data-id="element-172">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-173">
          <CheckIcon className="w-12 h-12 text-green-600" data-id="element-174" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-175">
          Intake Succesvol Verzonden!
        </h2>
        <p className="text-lg text-gray-600 mb-8" data-id="element-176">
          Bedankt voor uw tijd, {data.contactName}. We hebben uw gegevens in
          goede orde ontvangen op info@noorddigital.nl en een kopie naar{' '}
          {data.contactEmail} gestuurd. Wij nemen spoedig contact met u op.
        </p>

        <div className="bg-blue-50 p-8 rounded-3xl border border-blue-100" data-id="element-177">
          <h3 className="font-bold text-gray-900 mb-2" data-id="element-178">
            Bewaar een kopie voor uzelf
          </h3>
          <p className="text-sm text-gray-600 mb-6" data-id="element-179">
            U kunt uw volledige intake inclusief alle antwoorden downloaden als
            PDF voor uw eigen administratie.
          </p>
          <Button onClick={handleDownloadPDF} size="lg" className="shadow-md" data-id="element-180">
            <DownloadIcon className="w-5 h-5 mr-2" data-id="element-181" />
            Download Intake als PDF
          </Button>
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="max-w-4xl mx-auto space-y-8" data-id="element-182">
      <MascotPanel message="Geweldig, we zijn er! Controleer hieronder uw gegevens. Als alles klopt, kunt u de intake definitief indienen. U kunt daarna alles downloaden als PDF." data-id="element-183" />

      <div className="space-y-6" data-id="element-184">
        <SummarySection title="Contact & Bedrijf" step={2} data-id="element-185">
          <DataRow label="Naam" value={data.contactName} data-id="element-186" />
          <DataRow label="Functie" value={data.contactRole} data-id="element-187" />
          <DataRow label="E-mail" value={data.contactEmail} data-id="element-188" />
          <DataRow label="Telefoon" value={data.contactPhone} data-id="element-189" />
          <DataRow label="Bedrijfsnaam" value={data.companyName} data-id="element-190" />
          <DataRow label="KVK Nummer" value={data.cocNumber} data-id="element-191" />
          {data.multipleLocations === 'yes' ? <DataRow label="Aantal locaties" value={`${data.locations.length} locaties opgegeven`} data-id="element-192" /> : <DataRow label="Adres" value={`${data.mainStreet}, ${data.mainCity}`} data-id="element-193" />}
        </SummarySection>

        <SummarySection title="Verhaal & Branding" step={5} data-id="element-194">
          <DataRow label="Kernwaarden" value={data.coreValues} data-id="element-195" />
          <DataRow label="Huisstijl aanwezig" value={data.hasIdentity === 'yes_complete' ? 'Ja, compleet' : data.hasIdentity === 'yes_partly' ? 'Ja, deels' : 'Nee'} data-id="element-196" />
          <DataRow label="Typografie wens" value={data.typographyChoice} data-id="element-197" />
          <DataRow label="Merkkleuren" value={data.preferredColors.length > 0 ? data.preferredColors.map(c => c.name).join(', ') : ''} data-id="element-198" />
        </SummarySection>

        <SummarySection title="Website Doel & Structuur" step={8} data-id="element-199">
          <DataRow label="Primair doel" value={data.mainGoal} data-id="element-200" />
          <DataRow label="Type website" value={data.websiteType} data-id="element-201" />
          <DataRow label="Gewenste pagina's" value={[...data.neededPages, ...data.customPages].filter(p => p !== 'other').join(', ')} data-id="element-202" />
          <DataRow label="Content aanlevering" value={data.existingContent[0]} data-id="element-203" />
        </SummarySection>

        <SummarySection title="Techniek, SEO & Planning" step={12} data-id="element-204">
          <DataRow label="Domeinnaam" value={data.domainOwned === 'yes' ? data.domainName : 'Moet nog geregistreerd worden'} data-id="element-205" />
          <DataRow label="Hosting" value={data.hostingActive === 'noord' ? 'Via Noord Digital' : 'Zelf regelen'} data-id="element-206" />
          <DataRow label="Belang van SEO" value={data.seoServices} data-id="element-207" />
          <DataRow label="Gewenste live datum" value={data.launchDate} data-id="element-208" />
        </SummarySection>
      </div>

      <div className="bg-white rounded-3xl p-8 border border-gray-200 mt-12 shadow-sm" data-id="element-209">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-210">
          Klaar om te verzenden?
        </h2>

        <div className="max-w-xl mx-auto mb-8" data-id="element-211">
          <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors" data-id="element-212">
            <div className="flex-shrink-0 mt-0.5" data-id="element-213">
              <input type="checkbox" className="w-5 h-5 rounded border-gray-300 text-[#007AFF] focus:ring-[#007AFF]" checked={acceptedPrivacy} onChange={e => setAcceptedPrivacy(e.target.checked)} data-id="element-214" />
            </div>
            <div data-id="element-215">
              <p className="text-sm font-medium text-gray-900 flex items-center" data-id="element-216">
                <ShieldCheckIcon className="w-4 h-4 mr-1 text-green-600" data-id="element-217" />
                Privacy & AVG Akkoord
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-218">
                Ik ga ermee akkoord dat Noord Digital mijn ingevulde gegevens
                verwerkt en opslaat conform de AVG wetgeving om contact op te
                nemen over dit project. De gegevens worden veilig verstuurd.
              </p>
            </div>
          </label>
        </div>

        <div className="text-center" data-id="element-219">
          <Button size="lg" onClick={handleSubmit} disabled={!acceptedPrivacy || isSubmitting} className="px-12 py-4 text-lg shadow-xl w-full sm:w-auto" data-id="element-220">
            {isSubmitting ? <>
                <Loader2Icon className="w-5 h-5 mr-2 animate-spin" data-id="element-221" />
                Bezig met verzenden...
              </> : <>
                <SendIcon className="w-5 h-5 mr-2" data-id="element-222" />
                Intake Definitief Verzenden
              </>}
          </Button>
          <p className="text-xs text-gray-400 mt-4" data-id="element-223">
            Na het verzenden kunt u de volledige intake downloaden als PDF.
          </p>
        </div>
      </div>
    </motion.div>;
}